interface RegionDetectionResult {
  detectedRegion: string;
  locale: string;
  timezone: string;
}

/**
 * Map timezone to country code
 * More reliable than browser locale for region detection
 */
function getRegionFromTimezone(timezone: string): string {
  // Map timezones to regions
  // Priority African Countries
  if (timezone.startsWith('Africa/Lagos')) return 'NG';
  if (timezone.startsWith('Africa/Accra')) return 'GH';
  if (timezone.startsWith('Africa/Nairobi')) return 'KE';
  if (timezone.startsWith('Africa/Johannesburg')) return 'ZA';
  if (timezone.startsWith('Africa/Harare')) return 'ZW';
  if (timezone.startsWith('Africa/Addis_Ababa')) return 'ET';
  if (timezone.startsWith('Africa/Dakar')) return 'SN';
  if (timezone.startsWith('Africa/Abidjan')) return 'CI';
  if (timezone.startsWith('Africa/Kinshasa')) return 'CD';
  if (timezone.startsWith('Africa/Kigali')) return 'RW';
  if (timezone.startsWith('Africa/Lusaka')) return 'ZM';
  if (timezone.startsWith('Africa/Cairo')) return 'EG';

  // Americas
  if (timezone.startsWith('America/New_York') ||
    timezone.startsWith('America/Chicago') ||
    timezone.startsWith('America/Denver') ||
    timezone.startsWith('America/Los_Angeles') ||
    timezone.startsWith('America/Phoenix')) return 'US';
  if (timezone.startsWith('America/Toronto') ||
    timezone.startsWith('America/Vancouver')) return 'CA';

  // Europe
  if (timezone.startsWith('Europe/London')) return 'GB';
  if (timezone.startsWith('Europe/Zurich')) return 'CH'; // Switzerland
  if (timezone.startsWith('Europe/Stockholm')) return 'SE'; // Sweden
  if (timezone.startsWith('Europe/Oslo')) return 'NO'; // Norway
  if (timezone.startsWith('Europe/Copenhagen')) return 'DK'; // Denmark

  // Eurozone + General Europe (default to EU/EUR)
  if (timezone.startsWith('Europe/Paris') ||
    timezone.startsWith('Europe/Berlin') ||
    timezone.startsWith('Europe/Rome') ||
    timezone.startsWith('Europe/Madrid') ||
    timezone.startsWith('Atlantic/Canary') || // Spain (Canary Islands)
    timezone.startsWith('Africa/Ceuta') ||    // Spain (Ceuta)
    timezone.startsWith('Europe/Amsterdam') ||
    timezone.startsWith('Europe/Brussels') ||
    timezone.startsWith('Europe/Vienna') ||
    timezone.startsWith('Europe/Dublin') ||
    timezone.startsWith('Europe/Lisbon') || // Portugal also has Atlantic/Madeira
    timezone.startsWith('Atlantic/Madeira') ||
    timezone.startsWith('Europe/Athens') ||
    timezone.startsWith('Europe/Helsinki') ||
    timezone.startsWith('Europe/Warsaw') ||
    timezone.startsWith('Europe/Budapest') ||
    timezone.startsWith('Europe/Prague')) return 'EU'; // Generic EU for EUR currency

  // Fallback to browser locale
  const browserLocale = navigator.language || 'en-US';
  const localeParts = browserLocale.split('-');
  return localeParts[1] || 'US';
}

/**
 * Hook to detect user's region, locale, and timezone
 * Uses timezone-based detection which is more reliable than browser locale
 * Backend can also detect from IP as a fallback/override
 */
export function useRegionDetection(): RegionDetectionResult {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const detectedRegion = getRegionFromTimezone(timezone);

  return {
    detectedRegion,
    locale: navigator.language || 'en-US',
    timezone,
  };
}
